// VisitorList.jsx (Operator Dashboard - Fully Updated)
import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Typography, Container, Snackbar, Alert, Chip
} from "@mui/material";
import PhotoThumbnail from '../components/PhotoThumbnail';
import { fetchHostDetailsById, subscribeToVisitHistory } from "../db/visitorlistApi";

 const VisitorList = () => 
{
  const { currentUser: user } = useAuth();
  const navigate = useNavigate();

  const [visitors, setVisitors] = useState([]);
  const [tenants, setTenants] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const fetchHostDetails = useCallback(async (hostId) => {
    if (tenants[hostId]) return;
    try {
      const hostInfo = await fetchHostDetailsById(hostId);
      setTenants(prev => ({ ...prev, [hostId]: hostInfo }));
    } catch (error) {
      console.error("Error fetching host details:", error);
      setTenants(prev => ({ ...prev, [hostId]: { name: 'Error', companyName: 'Error' } }));
    }
  }, [tenants]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    const unsubscribe = subscribeToVisitHistory(
      user,
      async (snapshot) => {
        const visitData = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            const hostId = data.hostUserID || data.hostId || data.tenantId || 'N/A';
            const hostInfo = tenants[hostId] || await fetchHostDetailsById(hostId);
            return {
              id: doc.id,
              visitorName: data.visitorName || 'N/A',
              visitorEmail: data.visitorEmail || 'N/A',
              status: data.status || 'pending',
              visitorTime: data.visitorTime?.toDate() || null,
              createdAt: data.createdAt?.toDate() || null,
              photoUrl: data.photoUrl || null,
              visitorUserID: data.visitorUserID || null,
              hostUserID: hostId,
              hostName: hostInfo.name,
              companyName: hostInfo.companyName
            };
          })
        );
        setVisitors(visitData);
        setLoading(false);
      },
      (error) => {
        console.error("Failed to fetch visit history:", error);
        setError("Failed to load visitors. Please try again.");
        setSnackbar({ open: true, message: "Failed to load visitors.", severity: "error" });
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user, navigate, tenants, fetchHostDetails]);

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };

  return (
    <Container maxWidth="lg" sx={{ padding: "20px" }}>
      <Typography variant="h5" gutterBottom textAlign="center">
        {user?.role === "operator" ? "All Visit Logs" : "Your Visit Logs"}
      </Typography>

      {error && (
        <Typography color="primary" sx={{ marginBottom: 2, textAlign: "center" }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Typography align="center" sx={{ marginTop: 2 }}>
          <CircularProgress />
        </Typography>
      ) : visitors.length === 0 ? (
        <Typography sx={{ textAlign: "center", marginTop: 2 }}>
          No visit logs found.
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ marginTop: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Photo</strong></TableCell>
                <TableCell><strong>Visitor Name</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Host Name</strong></TableCell>
                <TableCell><strong>Company/Organization</strong></TableCell>
                <TableCell><strong>Date & Time</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visitors.map((visitor) => (
                <TableRow key={visitor.id}>
                  <TableCell>
                    <PhotoThumbnail
                      photoUrl={visitor.photoUrl}
                      visitorName={visitor.visitorName}
                      visitorUserID={visitor.visitorUserID}
                      visitorEmail={visitor.visitorEmail}
                      size={40}
                      variant="circular"
                      showZoom={true}
                      showCamera={false}
                    />
                  </TableCell>
                  <TableCell>{visitor.visitorName}</TableCell>
                  <TableCell>{visitor.visitorEmail}</TableCell>
                  <TableCell>{visitor.hostName}</TableCell>
                  <TableCell>{visitor.companyName}</TableCell>
                  <TableCell>{formatDateTime(visitor.visitorTime)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={visitor.status} 
                      color={visitor.status === "approved" ? "success" :
                             visitor.status === "rejected" ? "primary" : "warning"}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VisitorList;
